import { Dispatch, SetStateAction, useCallback } from 'react';

import Kpi from 'types/Kpi';
import { EmptyKpiData } from 'types/KpiData';

import { DirtyCell } from '../types';
import { areMonthsEqual, getFrequencyMonths } from '../utils';

interface Params {
    kpis: Kpi[];
    setKpis: Dispatch<SetStateAction<Kpi[]>>;
    setDirtyCell: Dispatch<SetStateAction<DirtyCell | null>>;
    pushDataToQueue: (kpiId: number, data: Kpi) => void;
}

const useKpiData = (params: Params) => {
    const { kpis, setKpis, setDirtyCell, pushDataToQueue } = params;

    const getDataForMonth = useCallback((index: number, month: number, year: number) => {
        const kpi = kpis.find(d => d.id === index);
        const monthData = (kpi?.data ?? []).find(k => k.month === month && k.year === year);

        if (!kpi || !monthData || monthData.amount === null || monthData.amount === '') return '';

        const value = monthData.amount * getFrequencyMonths(kpi.monitoringFrequency);

        return Math.round(value * 1000) / 1000;
    }, [kpis]);

    const setDataForMonth = useCallback((index: number, month: number, year: number, amount: number | '') => {
        setDirtyCell(null);

        const kpi = kpis.find(k => k.id === index);

        if (!kpi) return;

        const kpiData = kpi.data;
        let clonedKpiData = [...kpi.data];

        for (let i = month; i <= (month + getFrequencyMonths(kpi.monitoringFrequency) - 1); i++) {
            const monthData = kpiData.find(d => areMonthsEqual(d, { month: i, year }));

            const newMonthData =
                monthData ?
                    { ...monthData } :
                    {
                        ...EmptyKpiData,
                        month: i,
                        year,
                        generatedByFrontend: true
                    };

            newMonthData.amount = amount === '' ? amount : (amount / getFrequencyMonths(kpi.monitoringFrequency));

            clonedKpiData = clonedKpiData.filter(d => !areMonthsEqual(d, { month: i, year }));
            clonedKpiData.push(newMonthData);
        }

        const clonedKpi = { ...kpi, data: clonedKpiData };

        setKpis(data => {
            const clonedData = [...data];
            const realIndex = clonedData.findIndex(k => k.id === index);
            clonedData[realIndex] = clonedKpi;
            return clonedData;
        });

        pushDataToQueue(index, clonedKpi);
    }, [kpis, setKpis, pushDataToQueue, setDirtyCell]);

    const getKpiNote = useCallback((index: number) => {
        const kpi = kpis.find(d => d.id === index);

        return kpi?.note ?? '';
    }, [kpis]);

    const setKpiNote = useCallback((index: number, note: string) => {
        setDirtyCell(null);

        const kpi = kpis.find(k => k.id === index);

        if (!kpi) return;

        const clonedKpi = { ...kpi, note };

        setKpis(data => {
            const clonedData = [...data];
            const realIndex = clonedData.findIndex(k => k.id === index);
            clonedData[realIndex] = clonedKpi;
            return clonedData;
        });

        pushDataToQueue(index, clonedKpi);
    }, [kpis, setKpis, pushDataToQueue, setDirtyCell]);

    return { getDataForMonth, setDataForMonth, getKpiNote, setKpiNote };
};

export default useKpiData;